import type { CharacterClassId } from './character-roster';
import { N_, t, getIntlLocale } from '../i18n/i18n';

/**
 * design/gdd/talent-trees.md — the player-facing half of the talent trees:
 * a real name and a line of flavor per node, plus the icon it draws.
 *
 * The split here is deliberate and load-bearing. **Names and flavor are copy;
 * every number is computed from the `field`/`perRank` the server sends** with
 * the tree (see `effectLine`). The backend owns balance — `talent-defs.ts` is
 * the single source of those values — so a rebalance there changes what this
 * UI says without anyone remembering to edit it. Mirroring the numbers here
 * would create exactly the drift the HUD already shipped once ("110 / 100",
 * commit 1ea71f7) by keeping a second copy of a value the server owns.
 *
 * Before this file the tree rendered `nodeId.replace(/_/g, ' ')`, so a node
 * read "light feet 0/3" and nothing told the player what taking it would do.
 */

export interface TalentCopy {
  name: string;
  /** One line of voice. Never mechanical — the numbers come from the server. */
  flavor: string;
}

/**
 * Node ids are unique across all three trees, so this is one flat map rather
 * than a per-class nesting, and the icon filename is just `${nodeId}.png`.
 */
export const TALENT_COPY: Record<string, TalentCopy> = {
  // ── Knight (TANK) ──────────────────────────────────────────────
  fortitude: { name: N_('Fortitude'), flavor: N_('Old scars knit thicker than the skin they replaced.') },
  iron_will: { name: N_('Iron Will'), flavor: N_('The arm tires long after the mind refuses to.') },
  steady_guard: { name: N_('Steady Guard'), flavor: N_('Meet the blow squarely and it costs you less.') },
  brace_training: { name: N_('Brace Training'), flavor: N_('Drilled until the shield comes up before the thought does.') },
  unbreakable: { name: N_('Unbreakable'), flavor: N_('The line holds because someone decided it would.') },
  weapon_mastery: { name: N_('Weapon Mastery'), flavor: N_('A hundred repetitions, so the hundred-and-first needs no aiming.') },
  bulwark: { name: N_('Bulwark'), flavor: N_('Stone does not flinch, and neither will you.') },
  tireless_watch: { name: N_('Tireless Watch'), flavor: N_('The second hour of a watch is the one that matters.') },
  shield_wall: { name: N_('Shield Wall'), flavor: N_('One shield is cover. Three is a decision.') },
  castellan: { name: N_('Castellan'), flavor: N_('Someone has to carry the keep out on their back.') },
  grim_purpose: { name: N_('Grim Purpose'), flavor: N_('No flourish, no wasted swing. Just the work.') },
  stoic: { name: N_('Stoic'), flavor: N_('Complaint costs breath, and breath is for fighting.') },
  plated_stance: { name: N_('Plated Stance'), flavor: N_('Weight, placed well, is its own kind of answer.') },
  tempered_plate: { name: N_('Tempered Plate'), flavor: N_('Folded, quenched, folded again. The smith counted.') },
  warded_sigil: { name: N_('Warded Sigil'), flavor: N_('Chalk on the inside of the breastplate, where nobody looks.') },
  oiled_joints: { name: N_('Oiled Joints'), flavor: N_('Plate need not be slow. It only usually is.') },
  immovable_object: { name: N_('Immovable Object'), flavor: N_('They will have to move the floor instead.') },
  anvil_heart: { name: N_('Anvil Heart'), flavor: N_('Struck often enough to have stopped minding.') },
  long_watch: { name: N_('Long Watch'), flavor: N_('Dawn is a rumour. Stand anyway.') },
  wardens_burden: { name: N_('Warden\'s Burden'), flavor: N_('The heaviest sack goes to whoever complains least.') },
  adamant: { name: N_('Adamant'), flavor: N_('Not stubborn. Simply not available for the alternative.') },
  stand_fast: { name: N_('Stand Fast'), flavor: N_('The order was to hold. Nobody has countermanded it.') },
  unflinching: { name: N_('Unflinching'), flavor: N_('The shield stops shaking long before the arm does.') },
  oathkeeper: { name: N_('Oathkeeper'), flavor: N_('You said you would. That is the whole of the argument.') },
  guardians_resolve: { name: N_('Guardian\'s Resolve'), flavor: N_('While braced, you are the wall the Tallow breaks against.') },
  last_bastion: { name: N_('Last Bastion'), flavor: N_('With nothing left behind you, you stop moving backward.') },
  quartermaster: { name: N_('Quartermaster'), flavor: N_('Someone has to get it out. It was always going to be you.') },

  // ── Rogue (DPS) ────────────────────────────────────────────────
  reflexes: { name: N_('Reflexes'), flavor: N_('Breath returns quickest to those who never waste it.') },
  light_feet: { name: N_('Light Feet'), flavor: N_('The floor barely files a complaint.') },
  quick_hands: { name: N_('Quick Hands'), flavor: N_('The trick is finishing before anyone agrees you started.') },
  precision: { name: N_('Precision'), flavor: N_('Anatomy is a map with very few useful roads.') },
  shadow_training: { name: N_('Shadow Training'), flavor: N_('Dark is not a place to hide. It is a place to work.') },
  riposte_mastery: { name: N_('Riposte Mastery'), flavor: N_('Every parry is a question. This is the answer.') },
  silent_step: { name: N_('Silent Step'), flavor: N_('The room never learns you were in it.') },
  cutpurse: { name: N_('Cutpurse'), flavor: N_('Their pockets were only ever on loan.') },
  deft_hands: { name: N_('Deft Hands'), flavor: N_('Practised until the hands stopped waiting for permission.') },
  exposed_weakness: { name: N_('Exposed Weakness'), flavor: N_('Every suit of armour is a list of the places it isn\'t.') },
  second_wind: { name: N_('Second Wind'), flavor: N_('There is always one more. Finding it is the skill.') },
  evasion: { name: N_('Evasion'), flavor: N_('Not being there beats being armoured.') },
  soft_soles: { name: N_('Soft Soles'), flavor: N_('Felt over leather. Ruined by a week of this, and worth it.') },
  fleet: { name: N_('Fleet'), flavor: N_('The distance between two shadows, closed.') },
  wiry_frame: { name: N_('Wiry Frame'), flavor: N_('Nothing on you that is not doing a job.') },
  lithe: { name: N_('Lithe'), flavor: N_('Thin is not the same as frail, whatever the knight says.') },
  ghost_in_the_dark: { name: N_('Ghost in the Dark'), flavor: N_('They will argue afterwards about whether anyone was there.') },
  sharpened_edge: { name: N_('Sharpened Edge'), flavor: N_('A stone, an hour, and a great deal less effort later.') },
  counterpoint: { name: N_('Counterpoint'), flavor: N_('They committed. That was the mistake, not the swing.') },
  wardbreaker: { name: N_('Wardbreaker'), flavor: N_('Every charm has a seam. Put a knife in it.') },
  vanishing_act: { name: N_('Vanishing Act'), flavor: N_('The trick is not the leaving. It is the not-coming-back-yet.') },
  thief_of_hours: { name: N_('Thief of Hours'), flavor: N_('Time is only ever borrowed. You just borrow first.') },
  quick_wind: { name: N_('Quick Wind'), flavor: N_('Breathe on the way out, not on the way in.') },
  burglars_back: { name: N_('Burglar\'s Back'), flavor: N_('Load it high and walk like it weighs nothing.') },
  shadow_strike: { name: N_('Shadow Strike'), flavor: N_('Step out of nothing, and make the first one count.') },
  opportunist: { name: N_('Opportunist'), flavor: N_('Fair fights are for people with no imagination.') },
  cutthroat: { name: N_('Cutthroat'), flavor: N_('The unhurt are the easiest to hurt. Nobody has warned them yet.') },

  // ── Barbarian (DPS) ────────────────────────────────────────────
  brutality: { name: N_('Brutality'), flavor: N_('Subtlety is for people who missed.') },
  berserker_rage: { name: N_('Berserker Rage'), flavor: N_('Pain arrives late and finds nobody home.') },
  relentless: { name: N_('Relentless'), flavor: N_('Chains, walls, sense — all the same to a shoulder at speed.') },
  warcry_training: { name: N_('Warcry Training'), flavor: N_('The lungs are a weapon. Drill them like one.') },
  wider_reach: { name: N_('Wider Reach'), flavor: N_('Let the whole room hear what is coming.') },
  momentum: { name: N_('Momentum'), flavor: N_('Stopping was never the plan.') },
  thick_hide: { name: N_('Thick Hide'), flavor: N_('Scar tissue is armour you grew yourself.') },
  war_lungs: { name: N_('War Lungs'), flavor: N_('The shout is half the weapon. Feed it.') },
  pack_mule: { name: N_('Pack Mule'), flavor: N_('If it fits on your back, it leaves with you.') },
  savage_swing: { name: N_('Savage Swing'), flavor: N_('Aim for the middle and let the arc find the rest.') },
  unyielding: { name: N_('Unyielding'), flavor: N_('Falling over is a decision, and you have not made it.') },
  stampede: { name: N_('Stampede'), flavor: N_('Faster is also a kind of heavier.') },
  bull_neck: { name: N_('Bull Neck'), flavor: N_('A blow has to move you before it can stop you.') },
  scarred: { name: N_('Scarred'), flavor: N_('Each one was a lesson. You kept the notes.') },
  heavy_tread: { name: N_('Heavy Tread'), flavor: N_('The floor knows. Everything else can work it out.') },
  wild_gait: { name: N_('Wild Gait'), flavor: N_('Not running. Arriving, at a rate.') },
  earthshaker: { name: N_('Earthshaker'), flavor: N_('They planted their feet. The floor did not agree to help.') },
  boiling_blood: { name: N_('Boiling Blood'), flavor: N_('It gets worse the longer this goes on. For them.') },
  ox_heart: { name: N_('Ox Heart'), flavor: N_('Big, slow, and it has never once stopped.') },
  trophy_hauler: { name: N_('Trophy Hauler'), flavor: N_('You did not come this far to leave the good half.') },
  warcry_echo: { name: N_('Warcry Echo'), flavor: N_('The stone gives it back to you, louder.') },
  no_quarter: { name: N_('No Quarter'), flavor: N_('Asked for none. Not carrying any to give.') },
  thundering_charge: { name: N_('Thundering Charge'), flavor: N_('The room hears the decision before it sees it.') },
  hide_like_bark: { name: N_('Hide Like Bark'), flavor: N_('Years of weather, and a blade is only more weather.') },
  warlords_fury: { name: N_('Warlord\'s Fury'), flavor: N_('The shout lands, and for a while nothing survives contact.') },
  blood_frenzy: { name: N_('Blood Frenzy'), flavor: N_('The wound is the fuel. Keep going.') },
  bloodscent: { name: N_('Bloodscent'), flavor: N_('Something in you knows when it is nearly done, and speeds up.') },

  // ── Druid (HEALER) ─────────────────────────────────────────────
  wildheart: { name: N_('Wildheart'), flavor: N_('Something older than you keeps the beat.') },
  deep_roots: { name: N_('Deep Roots'), flavor: N_('What holds on below is what stands up above.') },
  gentle_step: { name: N_('Gentle Step'), flavor: N_('The ring of mushrooms is still unbroken behind you.') },
  mend_training: { name: N_('Mend Training'), flavor: N_('Knowing where to put your hands is most of it.') },
  widening_grove: { name: N_('Widening Grove'), flavor: N_('Reach far enough and nobody is out of reach.') },
  verdant_touch: { name: N_('Verdant Touch'), flavor: N_('Green closes over the wound before the blood dries.') },
  sap_and_bark: { name: N_('Sap and Bark'), flavor: N_('Take from the tree only what the tree can spare.') },
  grovekeeper: { name: N_('Grovekeeper'), flavor: N_('You have tended worse than this, and it lived.') },
  harvest: { name: N_('Harvest'), flavor: N_('Everything the dark grew, you can carry out.') },
  thorns: { name: N_('Thorns'), flavor: N_('Even the quiet things are armed.') },
  evergreen: { name: N_('Evergreen'), flavor: N_('Winter comes. It changes nothing.') },
  quiet_growth: { name: N_('Quiet Growth'), flavor: N_('Moss takes a keep without ever raising its voice.') },
  bark_skin: { name: N_('Bark Skin'), flavor: N_('What the wood learned about weather, it will teach you.') },
  mossfoot: { name: N_('Mossfoot'), flavor: N_('Green takes the sound before the stone can pass it on.') },
  ironbark: { name: N_('Ironbark'), flavor: N_('Some trees are only felled by people who brought the wrong axe.') },
  far_reach: { name: N_('Far Reach'), flavor: N_('Roots run further than the branches ever admit.') },
  heartwood: { name: N_('Heartwood'), flavor: N_('The dead centre of the trunk is the part that holds.') },
  sunlit_vigor: { name: N_('Sunlit Vigor'), flavor: N_('There is no sun down here. You brought some.') },
  warding_bloom: { name: N_('Warding Bloom'), flavor: N_('It opens when something wrong comes near, and closes after.') },
  root_walk: { name: N_('Root Walk'), flavor: N_('The floor offers a hand, if you ask the old way.') },
  first_flower: { name: N_('First Flower'), flavor: N_('The one that comes up through the ash and settles the argument.') },
  season_turn: { name: N_('Season Turn'), flavor: N_('Nothing waits as long as it thinks it will.') },
  deep_grove: { name: N_('Deep Grove'), flavor: N_('Far enough in that the edges stop mattering.') },
  forager: { name: N_('Forager'), flavor: N_('Everything here is either useful or a warning. Take both.') },
  lifebloom: { name: N_('Lifebloom'), flavor: N_('One mercy, opening twice.') },
  natures_ward: { name: N_('Nature\'s Ward'), flavor: N_('Stand near, and the wood stands with you.') },
  renewal: { name: N_('Renewal'), flavor: N_('Not the wound. The breath you need to keep standing over it.') },
};

/**
 * Copy for a node id, falling back to a prettified id.
 *
 * The fallback is not defensive padding — the server is the source of the
 * node list, so a tree rebalanced to add a node ships to a client that has
 * not been redeployed. That node must render as a usable, if plain, entry
 * rather than a blank tile. Same tolerance `computeTalentModifiers()` already
 * applies to unknown ids on the backend.
 */
export function talentCopy(nodeId: string): TalentCopy {
  const copy = TALENT_COPY[nodeId];
  // Translated here, at call time — the table holds English marked with N_().
  if (copy) return { name: t(copy.name), flavor: t(copy.flavor) };
  return {
    name: nodeId.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
    flavor: '',
  };
}

/** What each shared-vocabulary field is called in player words. */
const FIELD_LABEL: Record<string, string> = {
  hpBonus: N_('max health'),
  staminaBonus: N_('max stamina'),
  staminaRegenMultiplier: N_('stamina regen'),
  damageMultiplier: N_('damage'),
  cooldownMultiplier: N_('ability cooldown'),
  // 2026-10-16 — dodge went passive: the old dodge-cost nodes became these two.
  blockCostMultiplier: N_('block stamina cost'),
  dodgeChanceBonus: N_('dodge chance'),
  sprintSpeedMultiplier: N_('sprint speed'),
  riposteBonusMultiplier: N_('riposte bonus'),
  mendRadiusMultiplier: N_('Mend reach'),
  noiseRadiusMultiplier: N_('combat noise'),
  // The unit rides in the label, so the flat formatter needs no unit table.
  carryCapacityBonusKg: N_('kg carry capacity'),
  // The four the 7-tier expansion added (2026-09-04). Named after what a
  // player watches happen, not after the field: "physical armour" is the
  // number on the hub's armour line, and `poise` is the word the stagger
  // rules already use in the tooltip for BLUNT.
  physicalReductionBonus: N_('physical armour'),
  magicResistanceBonus: N_('magic resistance'),
  poiseBonus: N_('poise'),
  moveSpeedDelta: N_('move speed'),
};

/** talent-defs.ts's own `isMultiplierField` — flat additions, rendered as whole numbers. */
const FLAT_FIELDS = new Set(['hpBonus', 'staminaBonus', 'carryCapacityBonusKg', 'poiseBonus']);

/**
 * Flat on the server, but a **percentage point** to a player.
 *
 * These are stored the way `ARMOR` and `GearStats` store them — 0.015 means
 * one and a half points of mitigation, not one and a half percent of the base
 * — so they add like `FLAT_FIELDS` and render like multipliers. A third
 * category rather than a special case in either: rendering 0.045 as "+0"
 * (flat) or as "+4.5% more armour" (multiplier) are both wrong, and the second
 * is wrong in the way nobody catches, because it reads fine.
 */
const POINT_FIELDS = new Set(['physicalReductionBonus', 'magicResistanceBonus', 'moveSpeedDelta', 'dodgeChanceBonus']);

/**
 * `abilityPotencyMultiplier` is the one contextual field (talent-trees.md
 * Formula 1): it lengthens Vanish for a Rogue, lengthens Brace for a Knight
 * and widens Warcry's radius for a Barbarian. Naming it "ability potency"
 * would be accurate and useless, so it is named after what it actually moves.
 */
const POTENCY_LABEL: Partial<Record<CharacterClassId, string>> = {
  rogue: N_('Vanish duration'),
  knight: N_('Brace duration'),
  barbarian: N_('Warcry radius'),
  druid: N_('Mend healing'),
};

function fieldLabel(field: string, characterClass: CharacterClassId): string {
  if (field === 'abilityPotencyMultiplier') {
    const potency = POTENCY_LABEL[characterClass];
    return potency ? t(potency) : t('ability potency');
  }
  const label = FIELD_LABEL[field];
  return label ? t(label) : field;
}

/** `3.5` / `3,5` — one decimal only when the value needs it, in the player's locale. */
function formatAmount(n: number): string {
  return n.toLocaleString(getIntlLocale(), { maximumFractionDigits: 1, useGrouping: false });
}

/**
 * One line describing what `rank` ranks of this node are worth, e.g.
 * "+30 max health" or "−10% block stamina cost".
 *
 * Returns null when the server sent no `field`/`perRank` (a capstone, whose
 * effect is unique and lives in flavor, or a node shape this build predates).
 * Callers must treat null as "show flavor only" rather than rendering an
 * empty effect row.
 */
export function effectLine(
  field: string | undefined,
  perRank: number | undefined,
  rank: number,
  characterClass: CharacterClassId,
): string | null {
  if (!field || perRank === undefined || rank <= 0) return null;
  const total = perRank * rank;
  if (!Number.isFinite(total)) return null;
  const label = fieldLabel(field, characterClass);
  // U+2212 minus, not a hyphen — these sit next to digits.
  const sign = total < 0 ? '−' : '+';
  const magnitude = Math.abs(total);
  if (FLAT_FIELDS.has(field)) return t('{amount} {stat}', { amount: `${sign}${Math.round(magnitude)}`, stat: label });
  if (POINT_FIELDS.has(field)) {
    const points = Math.round(magnitude * 10000) / 100;
    return t('{amount}% {stat}', { amount: `${sign}${formatAmount(points)}`, stat: label });
  }
  // Multipliers are stored as a fraction (0.05 → 5%). Round to two decimals
  // BEFORE deciding whether a decimal is needed: 0.05 * 3 is 0.15000000000000002
  // in binary floating point, which is not an integer percent and would render
  // a cooldown node as "−15.0% ability cooldown".
  const pct = Math.round(magnitude * 10000) / 100;
  // One decimal only when the value genuinely needs it, so "+3% damage" does
  // not read "+3.0%".
  return t('{amount}% {stat}', { amount: `${sign}${formatAmount(pct)}`, stat: label });
}


/**
 * The icon a node falls back to when it has no art of its own (2026-09-04).
 *
 * The 7-tier expansion added 52 nodes and no pictures — the existing 56 were
 * generated in Higgsfield sheets and that spend was deliberately deferred. A
 * missing file would otherwise render as a browser's broken-image glyph, which
 * looks like a bug in the game rather than like art that has not arrived.
 *
 * Keyed by **effect field**, because that is the honest thing a fallback can
 * claim to depict: a node that grants max health borrows the heart already
 * drawn for Fortitude, and a carry node borrows Pack Mule's bundle. The tree
 * reads as a family until the individual art lands, which is exactly what it
 * is. Capstones are keyed by id instead — there are only four without art and
 * each has a near neighbour that means the same thing.
 *
 * This table becomes dead weight the moment every id has a file, and that is
 * the intended end state: `talentIconId` prefers the node's own art whenever
 * the request succeeds, so shipping `quartermaster.webp` silently retires its
 * entry here with no code change.
 */
const FIELD_ICON: Record<string, string> = {
  hpBonus: 'fortitude',
  staminaBonus: 'iron_will',
  staminaRegenMultiplier: 'tireless_watch',
  damageMultiplier: 'brutality',
  cooldownMultiplier: 'quick_hands',
  blockCostMultiplier: 'steady_guard',
  dodgeChanceBonus: 'light_feet',
  sprintSpeedMultiplier: 'momentum',
  abilityPotencyMultiplier: 'unbreakable',
  riposteBonusMultiplier: 'riposte_mastery',
  mendRadiusMultiplier: 'widening_grove',
  carryCapacityBonusKg: 'pack_mule',
  noiseRadiusMultiplier: 'silent_step',
  physicalReductionBonus: 'bulwark',
  magicResistanceBonus: 'guardians_resolve',
  poiseBonus: 'shield_wall',
  moveSpeedDelta: 'gentle_step',
};

/** The four capstones added without art, each borrowing the one nearest in meaning. */
const CAPSTONE_ICON: Record<string, string> = {
  quartermaster: 'pack_mule',
  cutthroat: 'precision',
  bloodscent: 'blood_frenzy',
  renewal: 'lifebloom',
};

/**
 * What to draw instead, if `${nodeId}.webp` does not load.
 *
 * Never null: `_point` — the talent-point glyph the header already uses — is
 * the last resort for a node this build has never heard of, which is a real
 * case (the server owns the node list and can ship a tree ahead of a client).
 */
export function talentIconFallback(nodeId: string, field?: string): string {
  return CAPSTONE_ICON[nodeId] ?? (field ? FIELD_ICON[field] : undefined) ?? '_point';
}
